import { useEffect, useState } from "react";
import { fetchKids, createKid, formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Settings } from "lucide-react";
import ParentSettingsSheet from "@/components/ParentSettingsSheet";

const COLORS = ["#FF5A36", "#FFC400", "#29C7E8", "#FF4D9D", "#7CE645"];

export default function KidPickerPage() {
  const { selectKid, logout } = useAuth();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    try {
      const data = await fetchKids();
      setKids(data);
    } catch (e) {
      toast.error("Couldn't load kid profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addKid = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const kid = await createKid(name.trim());
      setName("");
      setKids((prev) => [...prev, kid]);
      selectKid(kid.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="kid-picker-page"
      className="min-h-screen flex flex-col items-center px-4 py-8"
    >
      <div className="w-full max-w-md flex justify-end">
        <button
          data-testid="picker-settings-btn"
          onClick={() => setSettingsOpen(true)}
          className="w-11 h-11 rounded-full border-4 border-navy bg-white flex items-center justify-center hover:-translate-y-0.5 transition-transform"
          style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
          aria-label="Parent settings"
        >
          <Settings className="w-5 h-5 text-navy" strokeWidth={3} />
        </button>
      </div>

      <img
        src={LOGO_URL}
        alt="KIDS MONEY"
        className="h-24 sm:h-28 w-auto object-contain mb-4"
      />

      <div
        className="chunky-card w-full max-w-md p-6"
        style={{ backgroundColor: "#FFFDF6" }}
      >
        <h1 className="font-display text-3xl sm:text-4xl text-navy leading-tight">
          WHO'S USING THIS?
        </h1>
        <p className="font-display text-base text-navy/70 mt-1 mb-5">
          Tap your name to see your money.
        </p>

        {loading ? (
          <div className="font-display text-lg text-navy/60 py-6">Loading...</div>
        ) : (
          <>
            {kids.length === 0 ? (
              <div
                data-testid="no-kids-empty-state"
                className="font-display text-lg text-navy/70 rounded-2xl border-4 border-navy px-4 py-5 mb-5 text-center"
                style={{ backgroundColor: "#FFF3C2" }}
              >
                No kids yet! Add your first one below. 🪙
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-5">
                {kids.map((kid, idx) => (
                  <button
                    key={kid.id}
                    data-testid={`pick-kid-${kid.id}`}
                    onClick={() => selectKid(kid.id)}
                    className="chunky-btn py-5 px-3 flex flex-col items-center gap-1 text-navy"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  >
                    <span
                      className="w-14 h-14 rounded-full border-4 border-navy bg-white flex items-center justify-center font-display text-2xl text-navy"
                      style={{ boxShadow: "0 3px 0 0 #2A2D7C" }}
                    >
                      {kid.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="font-display text-xl mt-1 truncate max-w-full">
                      {kid.name}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={addKid} className="flex gap-2">
              <input
                data-testid="new-kid-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Add a kid (first name)"
                className="flex-1 min-w-0 font-display text-lg text-navy rounded-2xl border-4 border-navy px-4 py-3 outline-none bg-white"
                style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
                maxLength={30}
              />
              <button
                data-testid="add-kid-btn"
                type="submit"
                disabled={busy || !name.trim()}
                className="chunky-btn px-4 text-lg text-white flex items-center gap-1 disabled:opacity-50"
                style={{ backgroundColor: "#FF4D9D" }}
              >
                <Plus className="w-5 h-5" strokeWidth={4} />
                ADD
              </button>
            </form>
          </>
        )}
      </div>

      <button
        data-testid="picker-logout-btn"
        onClick={logout}
        className="mt-6 font-display text-base text-navy/60 underline"
      >
        Log out
      </button>

      <ParentSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onKidRenamed={load}
        onKidDeleted={load}
      />
    </div>
  );
}
